import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const NavBtn = ({ dir, onClick }) => {
  const Icon = dir === "prev" ? ChevronLeft : ChevronRight;
  return (
    <button type="button" onClick={onClick} aria-label={dir === "prev" ? "Previous photo" : "Next photo"} data-testid={`example-photo-${dir}`}
      className={`absolute top-1/2 -translate-y-1/2 ${dir === "prev" ? "left-2" : "right-2"} h-10 w-10 rounded-full bg-white/90 shadow-lg flex items-center justify-center transition-transform hover:scale-105`}>
      <Icon className="h-5 w-5" />
    </button>
  );
};

export const ExamplePhoto = ({ name, caption, group, className = "", children }) => {
  const photos = group || [[name, caption]];
  const start = Math.max(0, photos.findIndex(([n]) => n === name));
  const [idx, setIdx] = useState(start);
  const touchX = useRef(null);
  const [cur, curCap] = photos[idx];
  const many = photos.length > 1;
  const go = (d) => setIdx((i) => (i + d + photos.length) % photos.length);
  const onKey = (e) => {
    if (!many) return;
    if (e.key === "ArrowLeft") go(-1);
    if (e.key === "ArrowRight") go(1);
  };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - (touchX.current ?? 0);
    if (many && Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
  };

  return (
    <Dialog onOpenChange={(o) => { if (o) setIdx(start); }}>
      <DialogTrigger asChild>
        <button type="button" aria-label={`View ${caption}`} data-testid={`example-photo-open-${name}`} className={`cursor-zoom-in transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-kinetic ${className}`}>
          {children || <img src={`/examples/${name}.jpg`} alt={caption} loading="lazy" className="h-full w-full object-cover" />}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl p-3 sm:p-4" onKeyDown={onKey} data-testid="example-photo-dialog">
        <div className="relative select-none" onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }} onTouchEnd={onTouchEnd}>
          <img key={cur} src={`/examples/${cur}-lg.jpg`} alt={curCap} className="w-full max-h-[75vh] object-contain rounded-lg bg-slate-100 animate-fade-up" data-testid="example-photo-full" />
          {many && <NavBtn dir="prev" onClick={() => go(-1)} />}
          {many && <NavBtn dir="next" onClick={() => go(1)} />}
        </div>
        <div className="flex items-end justify-between gap-3 pt-1">
          <div>
            <DialogTitle className="text-sm font-medium" data-testid="example-photo-caption">{curCap}</DialogTitle>
            <p className="text-xs text-muted-foreground">Example photo{many ? " · swipe or use the arrows" : ""}</p>
          </div>
          {many && <span className="font-mono text-xs text-muted-foreground shrink-0" data-testid="example-photo-counter">{idx + 1} / {photos.length}</span>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
